import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "../client";
import type { DashboardAnalyticsPoint, DashboardCustomer, DashboardPayload, DashboardProductRow, DashboardRange, ProductVisibility } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";

export const dashboardQuery = (range: DashboardRange) =>
  queryOptions({
    queryKey: qk.dashboard.summary(range),
    queryFn: () => apiGet<DashboardPayload>(`/api/dashboard?range=${range}`),
    staleTime: 30_000, // plan §5: dashboard
  });

export const dashboardProductsQuery = () =>
  queryOptions({
    queryKey: qk.dashboard.products(),
    queryFn: () => apiGet<DashboardProductRow[]>("/api/dashboard/products"),
    staleTime: 30_000,
  });

export const dashboardCustomersQuery = () =>
  queryOptions({
    queryKey: qk.dashboard.customers(),
    queryFn: () => apiGet<DashboardCustomer[]>("/api/dashboard/customers"),
    staleTime: 30_000,
  });

export const dashboardAnalyticsQuery = (range: DashboardRange) =>
  queryOptions({
    queryKey: qk.dashboard.analytics(range),
    queryFn: () => apiGet<DashboardAnalyticsPoint[]>(`/api/dashboard/analytics?range=${range}`),
    staleTime: 30_000,
  });

export interface UpdateProductVisibilityVariables {
  productId: string;
  visibility: ProductVisibility;
}

export function useUpdateProductVisibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, visibility }: UpdateProductVisibilityVariables) =>
      apiPatch<DashboardProductRow>(`/api/dashboard/products/${productId}`, { visibility }),
    ...createOptimisticHandlers<UpdateProductVisibilityVariables>({
      queryClient,
      action: "update-product-visibility",
      affectedKeys: () => [qk.dashboard.products()],
      apply: ({ productId, visibility }) => [
        {
          queryKey: qk.dashboard.products(),
          updater: (previous: DashboardProductRow[] | undefined) =>
            previous?.map((row) => (row.id === productId ? { ...row, visibility } : row)),
        },
      ],
    }),
  });
}
